import { CircularProgress } from '@mui/material';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import { useState } from 'react';

import { DonutData } from '@/features/TimeStatsModel';
import { getTrackerUrl } from '@/utils/getTrackerUrl';

import { CustomTooltip } from './CustomTooltip';

import s from './TimePieChart.module.scss';

const PIE_SIZE = 300;
const LOADING_PIE_DATA = [{ name: '__loading__', value: 1 }];

type TimePieChartProps = {
  data: DonutData[];
  getColor: (item: DonutData, index: number) => string;
  reverse?: boolean;
  isLoading?: boolean;
};

const TimePieChart: React.FC<TimePieChartProps> = ({ data, getColor, reverse, isLoading }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [pieReady, setPieReady] = useState(false);

  return (
    <div
      className={s.time_dialog}
      style={{ flexDirection: reverse ? 'row-reverse' : 'row' }}
      data-loading={isLoading || undefined}
    >
      {!isLoading && (
        <div className={s.time_dialog__content}>
          {data.map((item, index) => {
            const color = getColor(item, index);
            const circle = <div className={s.time_dialog__circle} style={{ background: color }} />;

            return (
              <div
                className={s.time_dialog__item}
                key={item.name}
                onMouseEnter={() => pieReady && setActiveIndex(index)}
                onMouseLeave={() => pieReady && setActiveIndex(null)}
              >
                <div className={s.time_dialog__row}>
                  <div className={s.time_dialog__cell}>
                    {item.taskKey ? (
                      <>
                        {circle}
                        <a
                          href={getTrackerUrl(item.taskKey)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color, textDecoration: 'underline' }}
                        >
                          {item.taskKey}
                        </a>
                      </>
                    ) : (
                      <>
                        {circle}
                        <span style={{ color }}>{item.name}</span>
                      </>
                    )}
                  </div>
                  <div className={s.time_dialog__cell}>
                    {item.taskKey && <span>{item.name}</span>}
                  </div>
                  <div className={s.time_dialog__cell}>
                    <span className={s.time_dialog__time}>
                      {item.label} ({item.percent})
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={s.time_dialog__chart}>
        <PieChart width={PIE_SIZE} height={PIE_SIZE}>
          {isLoading ? (
            <Pie
              data={LOADING_PIE_DATA}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={140}
              dataKey="value"
              isAnimationActive={false}
              stroke="none"
            >
              <Cell fill="var(--light-secondary-color)" />
            </Pie>
          ) : (
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={140}
              dataKey="value"
              onAnimationEnd={() => setPieReady(true)}
            >
              {data.map((item, index) => (
                <Cell
                  key={item.name}
                  fill={getColor(item, index)}
                  className={
                    activeIndex === null ? '' : activeIndex === index ? s.active : s.inactive
                  }
                  onMouseEnter={() => pieReady && setActiveIndex(index)}
                  onMouseLeave={() => pieReady && setActiveIndex(null)}
                />
              ))}
            </Pie>
          )}
          {!isLoading && <Tooltip content={<CustomTooltip />} />}
        </PieChart>
        {isLoading && (
          <div className={s.time_dialog__loader}>
            <CircularProgress size={28} />
          </div>
        )}
      </div>
    </div>
  );
};

export { TimePieChart };
